import { snakeCase } from 'change-case';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAnalyticsIntegrationsContext } from '../AnalyticsIntegrationsProvider';
import { convertObjectKeysToSnakeCase } from '../utils';
import AnalyticsProxyContext, { type AnalyticsProxyCtx } from './context';
import type { AnalyticsSettings } from './types';
import {
  settingsContainValidDebuggingEnabledOption,
  settingsContainValidTrackingEnabledOption,
} from './utils';

export interface AnalyticsProxyProviderProps {
  autoInitialize?: boolean;
  settings?: Partial<AnalyticsSettings>;
}

const AnalyticsProxyProvider: React.FC<React.PropsWithChildren<AnalyticsProxyProviderProps>> = ({
  children,
  autoInitialize = false,
  settings,
}) => {
  const { integrations } = useAnalyticsIntegrationsContext();
  const [shouldInitialize, setShouldInitialize] = useState(autoInitialize);
  const trackingInitializedRef = useRef(false);
  const [trackingEnabled, setIsTrackingEnabled] = useState(settings?.trackingEnabled ?? false);
  const [debuggingEnabled, setIsDebuggingEnabled] = useState(settings?.debuggingEnabled ?? false);

  const integrationsRef = useRef(integrations);
  integrationsRef.current = integrations.filter((i) => i.enabled);

  const trackingEnabledRef = useRef(trackingEnabled);
  trackingEnabledRef.current = trackingEnabled;

  useEffect(() => {
    if (settingsContainValidTrackingEnabledOption(settings)) {
      setIsTrackingEnabled(settings.trackingEnabled);
    }
    if (settingsContainValidDebuggingEnabledOption(settings)) {
      setIsDebuggingEnabled(settings.debuggingEnabled);
    }
  }, [settings]);

  const trackEvent = useCallback<AnalyticsProxyCtx['trackEvent']>((rawEventName, rawEventProps) => {
    const eventName = snakeCase(rawEventName);
    const eventProps =
      typeof rawEventProps !== 'undefined' ? convertObjectKeysToSnakeCase(rawEventProps) : {};

    for (const integration of integrationsRef.current) {
      try {
        if (integration.settings?.useRawEventFormat === true) {
          integration.trackEvent?.(rawEventName, rawEventProps);
        } else {
          integration.trackEvent?.(eventName, eventProps);
        }
      } catch (e) {
        // swallow since a single integration should not break other integrations
      }
    }
  }, []);

  const trackPageView = useCallback<AnalyticsProxyCtx['trackPageView']>((...args) => {
    for (const integration of integrationsRef.current) {
      try {
        integration.trackPageView?.(...args);
      } catch (e) {
        // swallow since a single integration should not break other integrations
      }
    }
  }, []);

  const identifyUser = useCallback<AnalyticsProxyCtx['identifyUser']>((...args) => {
    for (const integration of integrationsRef.current) {
      try {
        integration.identifyUser?.(...args);
      } catch (e) {
        // swallow since a single integration should not break other integrations
      }
    }
  }, []);

  const resetUserIdentity = useCallback<AnalyticsProxyCtx['resetUserIdentity']>((...args) => {
    if (!trackingInitializedRef.current) return;

    for (const integration of integrationsRef.current) {
      try {
        integration.resetUserIdentity?.(...args);
      } catch (e) {
        // swallow since a single integration should not break other integrations
      }
    }
  }, []);

  const initialize = useCallback((initSettings?: Partial<AnalyticsSettings>) => {
    if (settingsContainValidTrackingEnabledOption(initSettings)) {
      setIsTrackingEnabled(initSettings.trackingEnabled);
    }
    if (settingsContainValidDebuggingEnabledOption(initSettings)) {
      setIsDebuggingEnabled(initSettings.debuggingEnabled);
    }

    setShouldInitialize(true);
  }, []);

  // initialize tracking
  useEffect(() => {
    if (shouldInitialize) {
      void Promise.all(
        integrationsRef.current.map(async (integration) => {
          try {
            const ignoreTrackingEnabledStatus =
              integration.settings?.ignoreTrackingEnabledStatus ?? false;

            if (ignoreTrackingEnabledStatus || trackingEnabled) {
              await integration.initialize();
              return integration;
            }
          } catch (e) {
            // swallow errors of initialization
          }
          return false;
        }),
      ).then((initializedIntegrations) =>
        Promise.all(
          initializedIntegrations.map(
            (integration) => integration && integration.onInitialization?.(integrationsRef.current),
          ),
        ),
      );
    }
  }, [shouldInitialize, trackingEnabled]);

  const contextValue = useMemo<AnalyticsProxyCtx>(
    () => ({
      initialize,
      trackPageView,
      trackEvent,
      identifyUser,
      resetUserIdentity,
      debuggingEnabled,
      trackingEnabled,
    }),
    [
      debuggingEnabled,
      identifyUser,
      initialize,
      resetUserIdentity,
      trackEvent,
      trackPageView,
      trackingEnabled,
    ],
  );

  return (
    <AnalyticsProxyContext.Provider value={contextValue}>{children}</AnalyticsProxyContext.Provider>
  );
};

export default AnalyticsProxyProvider;
